// src/components/BlindBidCard.jsx
// Tarjeta para pasar oferta ciega (vista usuario)

import React, { useState } from 'react';
import { Send, RefreshCw, Info } from 'lucide-react';
import { SemaphoreMessage, SemaphoreBadge } from './SemaphoreIndicator';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatNumber } from '../utils/formatCurrency';

/**
 * Tarjeta para enviar/actualizar oferta ciega
 */
const BlindBidCard = ({
  property,
  invite,
  userBlindOffer,
  canBlindBid,
  bidAmount,
  setBidAmount,
  validationError,
  setValidationError,
  handleBlindBidSubmit,
  conditionsText,
  setConditionsText,
}) => {
  const { t, i18n } = useTranslation();
  const [showConditions, setShowConditions] = useState(false);

  const hasExistingOffer = !!userBlindOffer;
  const isVerified = userBlindOffer?.isVerified;

  // ✅ SIMPLIFICADO: En fase BLIND, todos los invitados pueden ofertar
  // No hay restricción de canBlindBid - si tienes el token, puedes ofertar

  return (
    <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-purple-500/30">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-medium text-slate-300">{t('blind.startPrice')}</span>
        <span className="text-sm font-semibold text-white">
          {formatCurrency(property.startPrice, i18n.language)}
        </span>
      </div>

      {/* Oferta existente del usuario */}
      {hasExistingOffer && (
        <div className="mb-3">
          <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg mb-2">
            <div>
              <p className="text-xs text-slate-400">{t('blind.currentOffer')}</p>
              <p className="text-xl font-bold text-white">
                {formatCurrency(userBlindOffer.amount, i18n.language)}
              </p>
            </div>

            {/* Badge de semáforo con tooltip */}
            <div className="relative group">
              <SemaphoreBadge status={userBlindOffer.semaphoreStatus} />

              {/* Tooltip en hover */}
              <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-slate-900 border border-slate-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <SemaphoreMessage status={userBlindOffer.semaphoreStatus} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formulario */}
      <form onSubmit={handleBlindBidSubmit}>
        <div className="mb-3">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            {t('blind.submitLabel')}
          </label>
          <div className="relative">
            <input
              type="number"
              value={bidAmount}
              onChange={(e) => {
                setBidAmount(e.target.value);
                setValidationError('');
              }}
              placeholder={hasExistingOffer ? t('blind.placeholder') : formatNumber(property.startPrice, i18n.language)}
              className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              min="0"
              step="1"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">€</span>
          </div>

          {validationError && (
            <p className="mt-2 text-sm text-red-400">{validationError}</p>
          )}
        </div>

        {/* Comentarios opcionales */}
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setShowConditions(!showConditions)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300"
          >
            <Info className="w-4 h-4" />
            {showConditions ? t('bidForm.hideComments') : t('bidForm.addComments')}
          </button>

          {showConditions && (
            <textarea
              value={conditionsText}
              onChange={(e) => setConditionsText(e.target.value)}
              placeholder={t('bidForm.commentsPlaceholder')}
              className="mt-2 w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 text-sm resize-none"
              rows={3}
            />
          )}
        </div>

        {/* Botón submit */}
        <button
          type="submit"
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-all"
        >
          {hasExistingOffer ? (
            <>
              <RefreshCw className="w-5 h-5" />
              {t('blind.updateButton')}
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              {t('blind.submitButton')}
            </>
          )}
        </button>
      </form>

      {/* Nota informativa */}
      <p className="mt-4 text-xs text-slate-500 text-center">
        {t('blind.confidentialNote')}
      </p>
    </div>
  );
};

export default BlindBidCard;
